import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarDateInput from "../../../common/dashboard/CalendarDateInput";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Alert, Button, CircularProgress, TextField } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import {
  CmpInvoiceActionTypeEnum,
  CmpInvoiceDisplayStateEnum,
  CmpPaymentChoiceTypeEnum,
  UpdateCmpInvoiceDraftInput,
  useApproveCmpInvoiceMutation,
  useGetCmpInvoiceDetailQuery,
  useUpdateCmpInvoiceDraftMutation,
} from "../../../../graphql/types";
import { useCmpGlobalSettings } from "../../../../hooks/useCmpSettingsSection";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import { FormPageShell } from "../../../common/dashboard/FormPageShell";
import { hasUnsavedFormChanges } from "../../../common/formStateDirty";
import UnsavedChangesPrompt from "../../../common/UnsavedChangesPrompt";
import ActionSuccessDialog from "../../../common/dashboard/ActionSuccessDialog";
import { isValidInvoiceEmail } from "../CreateInvoice/createInvoiceHelpers";
import {
  formatBillingAddress,
  invoiceFromDetail,
  toBackendInvoicePartySnapshot,
  toBackendLineItems,
} from "../invoiceBackendAdapters";
import {
  chamberDateInputToUtcMilli,
  getChamberLocalDateInputValue,
} from "../invoiceDueDate";
import { invoicePageHeaderPresentation } from "../invoicePageHeaderPresentation";
import {
  getUpdateInvoiceFormDirtyState,
  UpdateInvoiceFormDirtyState,
} from "../invoiceFormDirtyState";
import {
  InvoiceDraftDocumentData,
  UnsavedInvoicePreviewModal,
} from "../ManageInvoices/ManageInvoiceActionModals";
import { manageInvoiceActionApolloContext } from "../ManageInvoices/manageInvoiceActionApolloContext";
import { runManageInvoiceActionOperation } from "../ManageInvoices/manageInvoiceActionOperation";
import {
  InvoiceHistory,
  InvoiceLineItemsEditor,
  InvoiceSection,
  InvoiceTotals,
  cpaGold,
  cpaGoldDark,
} from "../InvoiceMockComponents";
import {
  getInvoiceSubtotalInCents,
  getInvoiceTaxInCents,
  getInvoiceTotalInCents,
  InvoicePartySnapshot,
  MockInvoiceLineItem,
} from "../mockInvoiceData";
import {
  emptyInvoicePartySnapshot,
  InvoicePartyFieldErrors,
  InvoicePartyFields,
} from "../InvoicePartyFields";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../../utils/apolloErrorPolicy";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
  UserFacingError,
} from "../../../../utils/userFacingError";
import { EMAIL_FORMAT_MESSAGE } from "../../../../utils/fieldMessages";

const SAVE_DRAFT_FALLBACK = buildStaffErrorFallback("save the draft invoice");
// The draft has no invoice kind or source, so it cannot be previewed or
// created. Staff cannot fix it, so they refresh first, then email support.
const DRAFT_DETAILS_NOT_LOADED_TEXT = `Some details for this draft did not load. Refresh the page. If this message stays, email ${STAFF_SUPPORT_EMAIL}.`;
// The draft saved; the create step may or may not have finished.
const CREATE_AFTER_SAVE_UNCONFIRMED_TEXT = `Your changes were saved, but we could not confirm that the invoice was created. Refresh the page and check Invoices & Payments > Manage all Invoices & Payments before you try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;

type DraftUpdateRequest = Omit<UpdateCmpInvoiceDraftInput, "operationId"> & {
  operationId?: string;
};

type ValidationResult = {
  billToErrors: InvoicePartyFieldErrors;
  dueDateError: string;
  lineDescriptionErrors: Record<string, string>;
  message: string;
  referenceError: string;
  soldToErrors: InvoicePartyFieldErrors;
};

const invoicePartyFields: Array<keyof InvoicePartySnapshot> = [
  "customerName",
  "contactName",
  "contactEmail",
  "contactPhone",
  "addressLine1",
  "addressLine2",
  "city",
  "province",
  "postalCode",
  "country",
];

const areInvoicePartiesEqual = ({
  first,
  second,
}: {
  first: InvoicePartySnapshot;
  second: InvoicePartySnapshot;
}) =>
  invoicePartyFields.every(
    (field) =>
      first[field].trim().toLowerCase() === second[field].trim().toLowerCase(),
  );

// The address is optional for invoice issue (approved Oct 12 2026). The
// backend leaves missing address fields off the invoice.
const getPartyErrors = ({
  party,
}: {
  party: InvoicePartySnapshot;
}): InvoicePartyFieldErrors => {
  const errors: InvoicePartyFieldErrors = {};

  if (!party.customerName.trim()) {
    errors.customerName = "Enter the company or customer name.";
  }

  if (!party.contactEmail.trim()) {
    errors.contactEmail = "Enter the billing contact email address.";
  } else if (!isValidInvoiceEmail(party.contactEmail)) {
    errors.contactEmail = EMAIL_FORMAT_MESSAGE;
  }

  return errors;
};

const formatTaxRate = (rate: number) => String(Math.round(rate * 100) / 100);

const getDraftTaxLabel = ({
  lineItems,
  subtotalInCents,
  taxInCents,
}: {
  lineItems: MockInvoiceLineItem[];
  subtotalInCents: number;
  taxInCents: number;
}) => {
  const positiveTaxRates = Array.from(
    new Set(
      lineItems
        .map((lineItem) => lineItem.taxRate)
        .filter((rate) => Number.isFinite(rate) && rate > 0),
    ),
  );

  if (positiveTaxRates.length === 1) {
    return `Tax (${formatTaxRate(positiveTaxRates[0])}%)`;
  }

  if (subtotalInCents > 0 && taxInCents > 0) {
    return `Tax (${formatTaxRate((taxInCents / subtotalInCents) * 100)}%)`;
  }

  return "Tax";
};

const UpdateInvoice = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const setPageHeaderOverride = usePageHeaderOverride();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";

  useEffect(() => {
    setPageHeaderOverride({
      ...invoicePageHeaderPresentation,
      description: "Edit a draft invoice before it is issued.",
      title: "Edit Draft Invoice",
      withHeaderDivider: true,
    });

    return () => setPageHeaderOverride(null);
  }, [setPageHeaderOverride]);
  const {
    data: settingsData,
    error: settingsError,
    loading: settingsLoading,
  } = useCmpGlobalSettings(chamberId);
  const chamberTimeZone =
    settingsData?.getChamberMemberProSettings.setup.timeZone;
  const configuredManualPaymentMethods = useMemo(
    () =>
      settingsData?.getChamberMemberProSettings.paymentsAndTaxes
        .manualPaymentMethods || [],
    [settingsData],
  );
  const { data, error, loading, refetch } = useGetCmpInvoiceDetailQuery({
    variables: {
      input: {
        chamberId,
        invoiceId: invoiceId || "",
      },
    },
    skip: !chamberId || !invoiceId,
    fetchPolicy: "cache-and-network",
    // A failed read shows on this page, not the full-page error.
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  // UX audit INV-4: a rejected save or create stays on this page and in the
  // preview. Without the caller context the global Apollo error link sends
  // staff to /error500.
  const [updateCmpInvoiceDraft, { loading: updatingDraft }] =
    useUpdateCmpInvoiceDraftMutation({
      context: manageInvoiceActionApolloContext,
    });
  const [approveCmpInvoice, { loading: approvingInvoice }] =
    useApproveCmpInvoiceMutation({ context: manageInvoiceActionApolloContext });
  const detail = data?.getCmpInvoiceDetail || null;
  const invoice = useMemo(
    () => (detail ? invoiceFromDetail(detail) : null),
    [detail],
  );
  const [lineItems, setLineItems] = useState<MockInvoiceLineItem[]>([]);
  const [soldToParty, setSoldToParty] = useState<InvoicePartySnapshot>(
    emptyInvoicePartySnapshot,
  );
  const [billToParty, setBillToParty] = useState<InvoicePartySnapshot>(
    emptyInvoicePartySnapshot,
  );
  const [payerSameAsSoldTo, setPayerSameAsSoldTo] = useState(true);
  const [dueDate, setDueDate] = useState("");
  const [referencePurchaseOrder, setReferencePurchaseOrder] = useState("");
  const [publicMemo, setPublicMemo] = useState("");
  const [notes, setNotes] = useState("");
  const [formBaseline, setFormBaseline] =
    useState<UpdateInvoiceFormDirtyState | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Claude Design ActionSuccessModal content after a confirmed save.
  const [lastAction, setLastAction] = useState<{
    highlights: string[];
    message: string;
  } | null>(null);
  const [soldToErrors, setSoldToErrors] = useState<InvoicePartyFieldErrors>({});
  const [billToErrors, setBillToErrors] = useState<InvoicePartyFieldErrors>({});
  const [lineDescriptionErrors, setLineDescriptionErrors] = useState<
    Record<string, string>
  >({});
  const [dueDateError, setDueDateError] = useState("");
  const [referenceError, setReferenceError] = useState("");
  const initializedInvoiceVersionRef = useRef("");

  useEffect(() => {
    if (!invoice || !detail || !chamberTimeZone) {
      return;
    }

    const invoiceVersionKey = `${detail.invoice.id}:${
      detail.invoice.updatedAtUtcMilli || "missing"
    }:${chamberTimeZone}`;

    if (initializedInvoiceVersionRef.current === invoiceVersionKey) {
      return;
    }

    if (
      formBaseline &&
      hasUnsavedFormChanges({
        baseline: formBaseline,
        current: getUpdateInvoiceFormDirtyState({
          billToParty,
          dueDate,
          lineItems,
          notes,
          payerSameAsSoldTo,
          publicMemo,
          referencePurchaseOrder,
          soldToParty,
        }),
      })
    ) {
      // A background cache or network refresh must not replace active edits.
      // After a confirmed save moves the baseline, the clean form can accept
      // the new authoritative version on the next render.
      return;
    }

    initializedInvoiceVersionRef.current = invoiceVersionKey;
    const nextSoldToParty = invoice.soldToSnapshot || {
      ...emptyInvoicePartySnapshot(),
      customerName: invoice.customerName,
    };
    const nextBillToParty = invoice.billToSnapshot ||
      invoice.soldToSnapshot || {
        ...emptyInvoicePartySnapshot(),
        customerName: invoice.customerName,
        contactName: invoice.billingContactName,
        contactEmail: invoice.billingEmail,
        contactPhone: invoice.billingPhone,
        addressLine1: invoice.billingAddress,
      };
    const backendLineItems = detail.lineItems.length
      ? detail.lineItems
      : detail.invoice.lineItems || [];
    const nextLineItems = backendLineItems.map((lineItem) => ({
      accountNumber: lineItem.accountCode || undefined,
      code: lineItem.itemCode || undefined,
      description: lineItem.description,
      id: lineItem.id,
      quantity: lineItem.quantity,
      taxRate: lineItem.taxRatePercentage,
      unitPriceInCents: lineItem.unitPriceInCents,
    }));
    const nextPayerSameAsSoldTo =
      !detail.invoice.billToSnapshot ||
      areInvoicePartiesEqual({
        first: nextSoldToParty,
        second: nextBillToParty,
      });
    const nextDueDate = detail.invoice.dueAtUtcMilli
      ? getChamberLocalDateInputValue({
          nowUtcMilli: detail.invoice.dueAtUtcMilli,
          timeZone: chamberTimeZone,
        })
      : "";
    const nextReferencePurchaseOrder =
      detail.invoice.referencePurchaseOrder || "";
    const nextPublicMemo = invoice.publicMemo;
    const nextNotes = invoice.notes;

    setLineItems(nextLineItems);
    setSoldToParty(nextSoldToParty);
    setBillToParty(nextBillToParty);
    setPayerSameAsSoldTo(nextPayerSameAsSoldTo);
    setDueDate(nextDueDate);
    setReferencePurchaseOrder(nextReferencePurchaseOrder);
    setPublicMemo(nextPublicMemo);
    setNotes(nextNotes);
    setFormBaseline(
      getUpdateInvoiceFormDirtyState({
        billToParty: nextBillToParty,
        dueDate: nextDueDate,
        lineItems: nextLineItems,
        notes: nextNotes,
        payerSameAsSoldTo: nextPayerSameAsSoldTo,
        publicMemo: nextPublicMemo,
        referencePurchaseOrder: nextReferencePurchaseOrder,
        soldToParty: nextSoldToParty,
      }),
    );
    setSoldToErrors({});
    setBillToErrors({});
    setLineDescriptionErrors({});
    setDueDateError("");
    setReferenceError("");
  }, [
    billToParty,
    chamberTimeZone,
    detail,
    dueDate,
    formBaseline,
    invoice,
    lineItems,
    notes,
    payerSameAsSoldTo,
    publicMemo,
    referencePurchaseOrder,
    soldToParty,
  ]);

  const workingInvoice = useMemo(() => {
    if (!invoice) {
      return null;
    }

    const payer = payerSameAsSoldTo ? soldToParty : billToParty;

    return {
      ...invoice,
      billingAddress: formatBillingAddress(payer),
      billingContactName: payer.contactName || payer.customerName,
      billingEmail: payer.contactEmail,
      billingPhone: payer.contactPhone,
      billToSnapshot: payer,
      customerName: soldToParty.customerName,
      dueDate,
      lineItems,
      notes,
      payerSameAsSoldTo,
      publicMemo,
      soldToSnapshot: soldToParty,
    };
  }, [
    billToParty,
    dueDate,
    invoice,
    lineItems,
    notes,
    payerSameAsSoldTo,
    publicMemo,
    soldToParty,
  ]);
  const currentFormState = useMemo(
    () =>
      getUpdateInvoiceFormDirtyState({
        billToParty,
        dueDate,
        lineItems,
        notes,
        payerSameAsSoldTo,
        publicMemo,
        referencePurchaseOrder,
        soldToParty,
      }),
    [
      billToParty,
      dueDate,
      lineItems,
      notes,
      payerSameAsSoldTo,
      publicMemo,
      referencePurchaseOrder,
      soldToParty,
    ],
  );
  const hasUnsavedChanges = Boolean(
    formBaseline &&
      hasUnsavedFormChanges({
        baseline: formBaseline,
        current: currentFormState,
      }),
  );

  const subtotalInCents = workingInvoice
    ? getInvoiceSubtotalInCents(workingInvoice)
    : 0;
  const taxInCents = workingInvoice ? getInvoiceTaxInCents(workingInvoice) : 0;
  const totalInCents = workingInvoice
    ? getInvoiceTotalInCents(workingInvoice)
    : 0;
  const draftDocument = useMemo<InvoiceDraftDocumentData | null>(() => {
    if (
      !detail ||
      !workingInvoice ||
      !chamberTimeZone ||
      !detail.invoice.invoiceKind ||
      !detail.invoice.sourceKind
    ) {
      return null;
    }

    const payer = payerSameAsSoldTo ? soldToParty : billToParty;
    const paymentRules = detail.invoice.paymentRules;
    const allowedManualLabels =
      paymentRules?.allowedManualPaymentMethodLabels || [];
    const selectedManualMethod = configuredManualPaymentMethods.find(
      (method) =>
        method.id === detail.invoice.paymentChoice.manualPaymentMethodId,
    );
    const selectedManualLabel =
      detail.invoice.paymentChoice.manualPaymentMethodLabel ||
      selectedManualMethod?.label ||
      "";
    const manualLabels = Array.from(
      new Set([
        ...allowedManualLabels,
        ...(selectedManualLabel ? [selectedManualLabel] : []),
      ]),
    );
    const cardAllowed =
      paymentRules?.creditCardAllowed === true ||
      detail.invoice.paymentChoice.type === CmpPaymentChoiceTypeEnum.CreditCard;

    // The backend sends the member's company details for Sold To when they
    // differ from the saved billing party; otherwise the edited form prints.
    const companySoldTo =
      detail.soldToSnapshot &&
      JSON.stringify(detail.soldToSnapshot) !==
        JSON.stringify(detail.billingSnapshot)
        ? { ...detail.soldToSnapshot, customerName: soldToParty.customerName }
        : null;

    return {
      billToSnapshot: payer,
      billingSnapshot: soldToParty,
      soldToSnapshot: companySoldTo,
      customerName: payer.customerName || soldToParty.customerName,
      dueDate,
      eventName: detail.invoice.eventNameSnapshot,
      id: detail.invoice.id,
      invoiceDate: getChamberLocalDateInputValue({
        nowUtcMilli: detail.invoice.issuedAtUtcMilli,
        timeZone: chamberTimeZone,
      }),
      invoiceKind: detail.invoice.invoiceKind,
      invoiceNumber: detail.invoice.invoiceNumber,
      lineItems: lineItems.map((lineItem) => ({
        code: lineItem.code || null,
        description:
          lineItem.description.trim() || lineItem.code || "Invoice item",
        id: lineItem.id,
        quantity: lineItem.quantity,
        totalInCents: lineItem.quantity * lineItem.unitPriceInCents,
        unitPriceInCents: lineItem.unitPriceInCents,
      })),
      manualPaymentMethods: [
        ...(cardAllowed ? [{ enabled: true, label: "Credit Card" }] : []),
        ...manualLabels.map((label) => ({ enabled: true, label })),
      ],
      publicMemo: publicMemo.trim() || null,
      referencePurchaseOrder: referencePurchaseOrder.trim() || null,
      subtotalInCents,
      taxInCents,
      taxLabel: getDraftTaxLabel({
        lineItems,
        subtotalInCents,
        taxInCents,
      }),
      taxRegistrationNumber: detail.taxRegistrationNumber,
      totalInCents,
    };
  }, [
    billToParty,
    chamberTimeZone,
    configuredManualPaymentMethods,
    detail,
    dueDate,
    lineItems,
    payerSameAsSoldTo,
    publicMemo,
    referencePurchaseOrder,
    soldToParty,
    subtotalInCents,
    taxInCents,
    totalInCents,
    workingInvoice,
  ]);

  const clearValidationErrors = () => {
    setSoldToErrors({});
    setBillToErrors({});
    setLineDescriptionErrors({});
    setDueDateError("");
    setReferenceError("");
  };

  const markChanged = (change: () => void) => {
    change();
    clearValidationErrors();
    setActionError(null);
    setLastAction(null);
  };

  const validateForm = ({
    requirePreviewMetadata,
  }: {
    requirePreviewMetadata: boolean;
  }) => {
    const nextSoldToErrors = getPartyErrors({ party: soldToParty });
    const nextBillToErrors = payerSameAsSoldTo
      ? {}
      : getPartyErrors({ party: billToParty });
    const nextLineDescriptionErrors = lineItems.reduce<Record<string, string>>(
      (errors, lineItem) =>
        lineItem.description.trim()
          ? errors
          : {
              ...errors,
              [lineItem.id]: "Enter a description for this line item.",
            },
      {},
    );
    const dueAtUtcMilli = chamberDateInputToUtcMilli({
      dateInput: dueDate,
      timeZone: chamberTimeZone,
    });
    const nextDueDateError = !dueAtUtcMilli
      ? "Enter a valid due date."
      : detail && dueAtUtcMilli < detail.invoice.issuedAtUtcMilli
        ? "Select a Due Date on or after the invoice date."
        : "";
    const nextReferenceError =
      referencePurchaseOrder.trim().length > 100
        ? "Reference / PO must be 100 characters or less."
        : "";
    const hasPartyErrors =
      Object.keys(nextSoldToErrors).length > 0 ||
      Object.keys(nextBillToErrors).length > 0;
    let message = "";

    if (!lineItems.length) {
      message = "Add at least one line item with Add Line Item, then try again.";
    } else if (Object.keys(nextLineDescriptionErrors).length > 0) {
      message = "Enter a description for each line item.";
    } else if (totalInCents <= 0) {
      message =
        "The invoice total must be more than $0.00. Enter a unit price greater than $0.00 on at least one line item.";
    } else if (hasPartyErrors || nextDueDateError || nextReferenceError) {
      message = "Fix the highlighted invoice fields and try again.";
    } else if (
      requirePreviewMetadata &&
      (!detail?.invoice.invoiceKind || !detail.invoice.sourceKind)
    ) {
      message = DRAFT_DETAILS_NOT_LOADED_TEXT;
    }

    const validation: ValidationResult = {
      billToErrors: nextBillToErrors,
      dueDateError: nextDueDateError,
      lineDescriptionErrors: nextLineDescriptionErrors,
      message,
      referenceError: nextReferenceError,
      soldToErrors: nextSoldToErrors,
    };

    setSoldToErrors(validation.soldToErrors);
    setBillToErrors(validation.billToErrors);
    setLineDescriptionErrors(validation.lineDescriptionErrors);
    setDueDateError(validation.dueDateError);
    setReferenceError(validation.referenceError);
    setActionError(validation.message || null);

    return !validation.message;
  };

  const expectedUpdatedAtUtcMilli = detail?.invoice.updatedAtUtcMilli;
  const hasAuthoritativeVersion =
    typeof expectedUpdatedAtUtcMilli === "number" &&
    Number.isFinite(expectedUpdatedAtUtcMilli) &&
    expectedUpdatedAtUtcMilli > 0;
  const actionLoading = updatingDraft || approvingInvoice;

  if (
    !chamberId ||
    loading ||
    settingsLoading ||
    (!workingInvoice && !error) ||
    (!chamberTimeZone && !settingsError)
  ) {
    return (
      <div className="mx-auto flex max-w-4xl items-center justify-center gap-3 pb-10 text-sm text-gray-600">
        <CircularProgress size={20} />
        Loading invoice detail...
      </div>
    );
  }

  if (
    error ||
    settingsError ||
    !workingInvoice ||
    !detail ||
    !chamberTimeZone
  ) {
    return (
      <div className="mx-auto max-w-4xl pb-10">
        <Button
          className="mb-4 text-gray-700"
          component={RouterLink}
          startIcon={<ArrowBackIcon />}
          to={RoutePaths.chamberAdmin_invoicing_manage}
        >
          Back to Manage Invoices
        </Button>
        <Alert severity="warning">
          {error || settingsError
            ? getUserFacingErrorText(
                error || settingsError,
                buildStaffErrorFallback("load this invoice"),
              )
            : "We could not find this invoice. Go back to Manage Invoices and open it again."}
        </Alert>
      </div>
    );
  }

  const editAvailability = detail.availableActions.find(
    (action) => action.action === CmpInvoiceActionTypeEnum.Edit,
  );
  const isDraft = detail.displayState === CmpInvoiceDisplayStateEnum.Draft;
  const canEditDraft = isDraft && editAvailability?.available === true;
  const formEnabled = canEditDraft && hasAuthoritativeVersion && !actionLoading;

  if (!canEditDraft) {
    return (
      <FormPageShell
        contentClassName=""
        heading="Edit Invoice"
        headingId="update-invoice-detail"
      >
        <Button
          className="mb-4 text-gray-700"
          component={RouterLink}
          startIcon={<ArrowBackIcon />}
          to={RoutePaths.chamberAdmin_invoicing_manage}
        >
          Back to Manage Invoices
        </Button>
        <Alert severity="warning">
          {isDraft
            ? editAvailability?.reason ||
              `You cannot edit this draft invoice. If you need to change it, email ${STAFF_SUPPORT_EMAIL}.`
            : "Only a draft invoice can be edited. Use the Actions menu on Manage Invoices for this invoice status."}
        </Alert>
      </FormPageShell>
    );
  }

  const getUpdateRequest = (): DraftUpdateRequest => {
    const dueAtUtcMilli = chamberDateInputToUtcMilli({
      dateInput: dueDate,
      timeZone: chamberTimeZone,
    });

    if (!hasAuthoritativeVersion || !dueAtUtcMilli) {
      // Staff-ready text: the saved invoice details needed for a safe save
      // did not load.
      throw new UserFacingError(
        "We could not save this draft because some invoice details did not load. Refresh the page, then save again.",
      );
    }

    return {
      billToSnapshot: payerSameAsSoldTo
        ? null
        : toBackendInvoicePartySnapshot(billToParty),
      billingSnapshot: toBackendInvoicePartySnapshot(soldToParty),
      chamberId,
      dueAtUtcMilli,
      expectedUpdatedAtUtcMilli,
      internalNotes: notes.trim() || null,
      invoiceId: detail.invoice.id,
      isCmpInvoiceModuleTestData:
        detail.invoice.isCmpInvoiceModuleTestData ?? undefined,
      issuedAtUtcMilli: detail.invoice.issuedAtUtcMilli,
      lineItems: toBackendLineItems(lineItems),
      paymentChoice: {
        autoRenew: detail.invoice.paymentChoice.autoRenew,
        emailCommunicationConsentConfirmed:
          detail.invoice.paymentChoice.emailCommunicationConsentConfirmed,
        manualPaymentMethodId:
          detail.invoice.paymentChoice.manualPaymentMethodId || undefined,
        type: detail.invoice.paymentChoice.type,
      },
      publicMemo: publicMemo.trim() || null,
      referencePurchaseOrder: referencePurchaseOrder.trim() || null,
    };
  };

  const runDraftUpdate = async () => {
    const mutationResult = await runManageInvoiceActionOperation({
      action: "UPDATE_DRAFT",
      chamberId,
      execute: (input) =>
        updateCmpInvoiceDraft({
          variables: { input },
        }),
      input: getUpdateRequest(),
      invoiceId: detail.invoice.id,
      isAuthoritativelyCompleted: (result) =>
        result.data?.updateCmpInvoiceDraft.invoiceSavedSuccessfully === true &&
        result.data.updateCmpInvoiceDraft.invoice?.id === detail.invoice.id,
    });
    const savedDraft = mutationResult.data?.updateCmpInvoiceDraft;

    if (
      !savedDraft?.invoiceSavedSuccessfully ||
      savedDraft.invoice?.id !== detail.invoice.id
    ) {
      throw new Error("The draft invoice could not be saved.");
    }

    return savedDraft;
  };

  const handleSaveChanges = async ({
    fromPreview = false,
    showSuccess = true,
  }: {
    fromPreview?: boolean;
    showSuccess?: boolean;
  } = {}) => {
    if (
      !validateForm({
        requirePreviewMetadata: false,
      })
    ) {
      if (fromPreview) {
        setPreviewOpen(false);
      }
      return false;
    }

    try {
      setActionError(null);
      setLastAction(null);
      await runDraftUpdate();
      // The mutation confirmed this exact snapshot. A failed validation or
      // failed mutation never reaches this baseline update.
      setFormBaseline(currentFormState);
      if (fromPreview) {
        setPreviewOpen(false);
      }
      if (showSuccess) {
        const draftCustomerName =
          soldToParty.customerName.trim() || "the member";

        setLastAction(
          fromPreview
            ? {
                highlights: [draftCustomerName],
                message: `The draft invoice for ${draftCustomerName} has been saved. You can return to it at any time to review and create the invoice.`,
              }
            : {
                highlights: [],
                message: "Your changes have been saved successfully!",
              },
        );
      }

      try {
        await refetch();
      } catch {
        setActionError(
          "The draft was saved, but the page could not refresh. Refresh the page before you make more changes.",
        );
      }
      return true;
    } catch (mutationError) {
      setActionError(getUserFacingErrorText(mutationError, SAVE_DRAFT_FALLBACK));
      return false;
    }
  };

  const handleCreateInvoice = async () => {
    if (
      !validateForm({
        requirePreviewMetadata: true,
      })
    ) {
      setPreviewOpen(false);
      return;
    }

    let draftWasSaved = false;

    try {
      setActionError(null);
      await runDraftUpdate();
      draftWasSaved = true;
      // Approval is a second operation. If approval fails, the draft update
      // is still saved and must be the new clean baseline.
      setFormBaseline(currentFormState);
      const result = await approveCmpInvoice({
        variables: {
          input: {
            chamberId,
            invoiceId: detail.invoice.id,
            isCmpInvoiceModuleTestData:
              detail.invoice.isCmpInvoiceModuleTestData ?? undefined,
            sendPaymentRequest: false,
          },
        },
      });

      if (!result.data?.approveCmpInvoice.actionCompletedSuccessfully) {
        throw new UserFacingError(
          "Your changes were saved, but the invoice was not created. Refresh the page, then click Approve/Create Invoice again.",
        );
      }

      setPreviewOpen(false);
      navigate(RoutePaths.chamberAdmin_invoicing_manage);
    } catch (mutationError) {
      if (draftWasSaved) {
        try {
          await refetch();
        } catch {
          // The error below already requires a refresh before another action.
        }
      }

      setPreviewOpen(false);
      setActionError(
        getUserFacingErrorText(
          mutationError,
          draftWasSaved
            ? CREATE_AFTER_SAVE_UNCONFIRMED_TEXT
            : SAVE_DRAFT_FALLBACK,
        ),
      );
    }
  };

  return (
    <FormPageShell
      alerts={
        <>
          {!hasAuthoritativeVersion ? (
            <Alert className="mb-5" severity="warning">
              Some details for this draft did not load. Refresh the page before
              you make changes. If this message stays, email{" "}
              {STAFF_SUPPORT_EMAIL}.
            </Alert>
          ) : null}
          {!draftDocument ? (
            <Alert className="mb-5" severity="warning">
              {DRAFT_DETAILS_NOT_LOADED_TEXT}
            </Alert>
          ) : null}
        </>
      }
      beforeMain={
        <UnsavedChangesPrompt
          when={hasUnsavedChanges}
          saving={updatingDraft || approvingInvoice}
          onSave={() => handleSaveChanges({ showSuccess: false })}
          saveError={actionError || undefined}
        />
      }
      contentClassName=""
      heading="Edit Invoice"
      headingId="update-invoice-detail"
    >
      <div className="mb-5 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <Button
          className="text-gray-700"
          component={RouterLink}
          startIcon={<ArrowBackIcon />}
          to={RoutePaths.chamberAdmin_invoicing_manage}
        >
          Back to Manage Invoices
        </Button>
        <div className="rounded border border-gray-200 bg-white px-4 py-3 text-sm shadow-sm">
          <p className="font-bold text-gray-950">Invoice total</p>
          <p className="mt-1 text-xl font-bold text-gray-950">
            {new Intl.NumberFormat("en-CA", {
              currency: "CAD",
              style: "currency",
            }).format(totalInCents / 100)}
          </p>
        </div>
      </div>

      <div className="space-y-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-7">
        <InvoiceSection label="Invoice" title="Draft Invoice Details">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <TextField
              disabled
              label="Invoice Number"
              value={workingInvoice.invoiceNumber}
            />
            <TextField
              disabled
              label="Customer"
              value={workingInvoice.customerName}
            />
            <TextField
              disabled
              label="Source"
              value={workingInvoice.sourceLabel}
            />
            <TextField
              disabled={!formEnabled}
              error={Boolean(referenceError)}
              fullWidth
              helperText={referenceError || undefined}
              inputProps={{ maxLength: 100 }}
              label="Reference / PO"
              onChange={({ target }) =>
                markChanged(() => setReferencePurchaseOrder(target.value))
              }
              value={referencePurchaseOrder}
            />
          </div>
        </InvoiceSection>

        <InvoiceSection label="Billing" title="Sold-to And Bill-to Details">
          <div className="space-y-6">
            <div>
              <h3 className="mb-3 text-sm font-extrabold uppercase tracking-wide text-gray-700">
                Sold To
              </h3>
              <InvoicePartyFields
                disabled={!formEnabled}
                errors={soldToErrors}
                idPrefix="update-invoice-sold-to"
                onChange={(nextParty) =>
                  markChanged(() => setSoldToParty(nextParty))
                }
                value={soldToParty}
              />
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h3 className="text-sm font-extrabold uppercase tracking-wide text-gray-700">
                Who is paying?
              </h3>
              <div className="mt-3 inline-flex rounded-md border border-gray-300 bg-white p-1">
                <button
                  aria-pressed={payerSameAsSoldTo}
                  className={`rounded px-4 py-2 text-sm font-bold ${
                    payerSameAsSoldTo
                      ? "bg-cpaGold text-white"
                      : "text-gray-600"
                  } disabled:cursor-not-allowed`}
                  disabled={!formEnabled}
                  onClick={() => markChanged(() => setPayerSameAsSoldTo(true))}
                  type="button"
                >
                  Same as sold-to
                </button>
                <button
                  aria-pressed={!payerSameAsSoldTo}
                  className={`rounded px-4 py-2 text-sm font-bold ${
                    !payerSameAsSoldTo
                      ? "bg-cpaGold text-white"
                      : "text-gray-600"
                  } disabled:cursor-not-allowed`}
                  disabled={!formEnabled}
                  onClick={() => markChanged(() => setPayerSameAsSoldTo(false))}
                  type="button"
                >
                  Different payer
                </button>
              </div>
              {!payerSameAsSoldTo ? (
                <div className="mt-4">
                  <InvoicePartyFields
                    disabled={!formEnabled}
                    errors={billToErrors}
                    idPrefix="update-invoice-bill-to"
                    onChange={(nextParty) =>
                      markChanged(() => setBillToParty(nextParty))
                    }
                    value={billToParty}
                  />
                </div>
              ) : null}
            </div>

            <div>
              <label
                htmlFor="update-invoice-due-date"
                className="mb-1 block text-sm font-bold text-gray-900"
              >
                Due Date <span className="text-red-500">*</span>
              </label>
              <CalendarDateInput
                id="update-invoice-due-date"
                ariaLabel="Due Date"
                mode="fullDate"
                iconPosition="left"
                disabled={!formEnabled}
                error={Boolean(dueDateError)}
                value={dueDate}
                onChange={(value) => markChanged(() => setDueDate(value))}
              />
              {dueDateError ? (
                <p className="mt-1 text-[12px] text-[#E24C4C]">{dueDateError}</p>
              ) : null}
            </div>
          </div>
        </InvoiceSection>

        <InvoiceSection label="Items" title="Invoice Line Items">
          <InvoiceLineItemsEditor
            descriptionErrors={lineDescriptionErrors}
            lineItems={lineItems}
            onChange={(nextLineItems) =>
              markChanged(() => setLineItems(nextLineItems))
            }
            readOnly={!formEnabled}
          />
          <div className="mt-5">
            <InvoiceTotals invoice={workingInvoice} />
          </div>
        </InvoiceSection>

        <InvoiceSection label="Notes" title="Memo And Staff Notes">
          <div className="grid grid-cols-1 gap-4">
            <TextField
              disabled={!formEnabled}
              fullWidth
              label="Customer Memo"
              minRows={2}
              multiline
              onChange={({ target }) =>
                markChanged(() => setPublicMemo(target.value))
              }
              value={publicMemo}
            />
            <TextField
              disabled={!formEnabled}
              fullWidth
              label="Internal Notes"
              minRows={3}
              multiline
              onChange={({ target }) =>
                markChanged(() => setNotes(target.value))
              }
              value={notes}
            />
          </div>
        </InvoiceSection>

        <InvoiceSection label="History" title="Activity History">
          <InvoiceHistory invoice={workingInvoice} />
        </InvoiceSection>

        <div className="sticky bottom-0 z-10 -mx-2 border-t border-gray-200 bg-white/95 px-2 py-4 backdrop-blur">
          {actionError ? (
            <Alert className="mb-3" severity="error">
              {actionError}
            </Alert>
          ) : null}

          <div className="flex flex-col justify-end gap-3 sm:flex-row">
            <Button
              disabled={!formEnabled || !draftDocument}
              onClick={() => {
                setActionError(null);
                setLastAction(null);
                setPreviewOpen(true);
              }}
              startIcon={<VisibilityOutlinedIcon />}
              variant="outlined"
            >
              Preview Invoice
            </Button>
            <Button
              disabled={!formEnabled}
              onClick={() => void handleSaveChanges()}
              startIcon={<SaveOutlinedIcon />}
              sx={{
                backgroundColor: cpaGold,
                "&:hover": { backgroundColor: cpaGoldDark },
              }}
              variant="contained"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {draftDocument ? (
        <UnsavedInvoicePreviewModal
          actionLoading={actionLoading}
          chamber={chamber}
          draftDocument={draftDocument}
          errorMessage={actionError}
          onClose={() => setPreviewOpen(false)}
          onCreateInvoice={() => void handleCreateInvoice()}
          onSaveDraft={async () => {
            await handleSaveChanges({ fromPreview: true });
          }}
          open={previewOpen}
        />
      ) : null}

      <ActionSuccessDialog
        highlights={lastAction?.highlights}
        message={lastAction?.message}
        onClose={() => setLastAction(null)}
      />
    </FormPageShell>
  );
};

export default UpdateInvoice;
